import {
  useCallback,
  useMemo,
} from 'react'
import {
  QueryClient,
  useIsMutating,
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { isForbiddenError } from '@api/errors'
// import camelcaseKeys from 'camelcase-keys'
import backend from '@api/backend'
import {
  notifyApiError,
  notifySuccess,
} from '@common/utils/notify'
import Device, {
  DeviceAPI,
  DeviceBase,
  DeviceIDTypes,
  DeviceValvesUpdate,
  NewDevice,
  StagedDevice,
  StagedDeviceAPI,
  TestDevice,
  TestDeviceAPI,
  Timezone,
  ValveRecoveryPolicy,
  ValveSettingsAPI,
  ValveStateExtendedSlots,
  ValvesState,
} from '@data/device/model/device'
import { DeviceAdd } from '@data/device/model/deviceAdd'
import { ValveStatusAPI } from '@data/device/model/valveStatus'
import {
  applyValveStateExtended,
  mapDevice,
  mapUnprovisionedDevice,
} from './mapDevice'
import {
  ValveSchedule,
  ValveScheduleCreate,
} from '@data/device/model/valveSchedule'
import { TokenPermission } from '@data/rbac/model/rbac'
import { useSelectedAccountIds } from '@data/accounts/accounts'
import { useSelectedLocations } from '@data/locations/locations'
import { useAuthStore } from '@store/auth.store'
import { useDeviceStore } from '@store/device.store'

const EMPTY_DEVICES: Device[] = []

export const DEVICES_QUERY_KEY = ['devices'] as const
export const TIMEZONES_QUERY_KEY = ['devices', 'timezones'] as const
export const TEST_DEVICES_QUERY_KEY = ['devices', 'test'] as const
export const STAGED_DEVICES_QUERY_KEY = ['devices', 'staged'] as const
export const DEVICE_WRITE_MUTATION_KEY = ['devices', 'write'] as const
export const DEVICE_MESSAGE_MUTATION_KEY = ['devices', 'message'] as const
export const valveSettingsQueryKey = (dUuid: string) => ['valveSettings', dUuid] as const

const patchDevice = (
  queryClient: QueryClient,
  dUuid: string,
  patch: (device: Device) => Device,
) => {
  queryClient.setQueryData<Device>(['devices', 'DeviceId', dUuid], (device) =>
    device ? patch(device) : device,
  )
  queryClient.setQueryData<Device[]>(DEVICES_QUERY_KEY, (devices) =>
    devices?.map((device) => (device.dUuid === dUuid ? patch(device) : device)),
  )
}

export const setDevice = (
  queryClient: QueryClient,
  updatedDevice: Device,
  previousDUuid = updatedDevice.dUuid,
) => {
  queryClient.setQueryData<Device>(
    ['devices', 'DeviceId', updatedDevice.dUuid],
    updatedDevice,
  )
  queryClient.setQueryData<Device[]>(DEVICES_QUERY_KEY, (devices) => {
    if (!devices) return devices

    const existingIndex = devices.findIndex((device) => device.dUuid === previousDUuid)

    if (existingIndex === -1) return [...devices, updatedDevice]

    return devices.map((device) => (device.dUuid === previousDUuid ? updatedDevice : device))
  })
}

interface DeviceDataUpdate {
  deviceId: string
  idType: DeviceIDTypes
  device: Device
}

interface DevicePulsesUpdate {
  deviceId: string
  idType: DeviceIDTypes
  pulsesPerLiter: number
}

interface DeviceValvesStateUpdate {
  deviceId: string
  idType: DeviceIDTypes
  valves: ValvesState
}

interface DeviceByIdRequest {
  deviceId: string
  idType: DeviceIDTypes
}

interface FirmwareUpdate {
  deviceId: string
  idType: DeviceIDTypes
  firmwareVersion: string
}

interface TestDeviceFirmwareUpdate {
  deviceId: string
  binFileName: string
}

const updateDeviceData = async ({
  deviceId,
  idType,
  device,
}: DeviceDataUpdate): Promise<DeviceAPI> => {
  const response = await backend.put<DeviceAPI>(
    `/devices/${deviceId}`,
    { device_update: device.deviceSettings },
    { params: { idType } },
  )
  return response.data
}

const updateDevicePulses = async ({
  deviceId,
  idType,
  pulsesPerLiter,
}: DevicePulsesUpdate): Promise<DeviceAPI> => {
  const response = await backend.patch<DeviceAPI>(
    `/devices/${deviceId}/set_pulses`,
    { pulses: pulsesPerLiter },
    { params: { idType } },
  )
  return response.data
}

const updateDeviceValves = async ({
  deviceId,
  idType,
  valves,
}: DeviceValvesStateUpdate): Promise<ValvesState> => {
  const response = await backend.put<ValvesState>(
    `/devices/${deviceId}/change_valve`,
    valves,
    { params: { idType } },
  )
  return response.data
}

const refreshDevice = async ({ deviceId, idType }: DeviceByIdRequest): Promise<DeviceAPI> => {
  const response = await backend.get<DeviceAPI>(`/devices/${deviceId}`, {
    params: { idType },
  })
  return response.data
}

// `/devices` and `/devices/{device_id}` are both scoped to READ:DEVICE:PROD. Gating on it
// keeps users without the scope from firing a request that can only 403, while the
// `authenticated` half closes the mapping race: `mapDevice` reads permissions for
// `connectionStatus`, so a fetch resolving before `applyToken` would cache ordinary-user
// thresholds under `staleTime: Infinity` for the rest of the session.
export const useCanReadProdDevices = () =>
  useAuthStore((state) =>
    state.authenticated &&
    state.permissions.some((permission) => permission.name === 'READ:DEVICE:PROD'),
  )

export const useDevicesQuery = () => {
  const permissions = useAuthStore((state) => state.permissions)
  const canReadProdDevices = useCanReadProdDevices()

  return useQuery<Device[]>({
    queryKey: DEVICES_QUERY_KEY,
    queryFn: async ({ signal }) => {
      const response = await backend.get<DeviceAPI[]>('/devices', { signal })
      return response.data.map((device) => mapDevice(device, permissions))
    },
    enabled: canReadProdDevices,
    staleTime: Infinity,
  })
}

// `/devices/timezones` is scoped to READ:DEVICE:PROD, so this reuses the device gate
// rather than the ACCOUNT:ADMIN:SMARTFLOW it used to ask for — that mismatch left a device
// admin without the Smartflow scope looking at an empty timezone dropdown.
//
// `gcTime: Infinity` matters only because this query is no longer warmed by
// `AppDataBootstrap`. Without the bootstrap observer holding the cache, `staleTime` alone
// would let the default five-minute collection drop the list every time both modals close.
// The IANA zone list does not need re-collecting.
export const useTimezonesQuery = () => {
  const canReadProdDevices = useCanReadProdDevices()

  return useQuery<Timezone[]>({
    queryKey: TIMEZONES_QUERY_KEY,
    queryFn: async ({ signal }) => {
      const response = await backend.get<Timezone[]>('/devices/timezones', { signal })
      return response.data
    },
    enabled: canReadProdDevices,
    staleTime: Infinity,
    gcTime: Infinity,
  })
}

export const useTestDevicesQuery = () => {
  const isSessionReady = useAuthStore((state) => state.authenticated)
  const canReadTestDevices = useAuthStore((state) =>
    state.permissions.some((permission) => permission.name === 'READ:DEVICE:TEST'),
  )

  return useQuery<TestDevice[]>({
    queryKey: TEST_DEVICES_QUERY_KEY,
    queryFn: async ({ signal }) => {
      const response = await backend.get<TestDeviceAPI[]>('/test_devices/', { signal })
      return response.data.map(mapUnprovisionedDevice)
    },
    enabled: isSessionReady && canReadTestDevices,
    staleTime: Infinity,
  })
}

const useCanReadStagedDevices = () =>
  useAuthStore((state) =>
    state.permissions.some((permission) => permission.name === 'READ:DEVICE:STGD'),
  )

export const useStagedDevicesQuery = () => {
  const isSessionReady = useAuthStore((state) => state.authenticated)
  const canReadStagedDevices = useCanReadStagedDevices()

  return useQuery<StagedDevice[]>({
    queryKey: STAGED_DEVICES_QUERY_KEY,
    queryFn: async ({ signal }) => {
      const response = await backend.get<StagedDeviceAPI[]>('/devices/staged_devices', {
        signal,
      })
      return response.data.map(mapUnprovisionedDevice)
    },
    enabled: isSessionReady && canReadStagedDevices,
    staleTime: Infinity,
  })
}

export const useDeviceById = (deviceId: string, enable: boolean, permissions: TokenPermission[]) => {
  const canReadProdDevices = useCanReadProdDevices()

  return useQuery<Device>({
    queryKey: ['devices', 'DeviceId', deviceId],
    queryFn: ({ signal }) => getByDeviceId(deviceId, permissions, signal),
    enabled: !!deviceId && enable && canReadProdDevices,
    staleTime: 1000 * 60,
  })
}

const showDeviceError = (error: unknown) => {
  notifyApiError(error)
}

export const useDeviceDataUpdate = () => {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((state) => state.permissions)

  return useMutation({
    mutationKey: DEVICE_WRITE_MUTATION_KEY,
    mutationFn: updateDeviceData,
    onSuccess: (deviceData) => {
      setDevice(queryClient, mapDevice(deviceData, permissions))
      notifySuccess('Device updated successfully')
    },
    onError: showDeviceError,
  })
}

export const useDevicePulsesUpdate = () => {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((state) => state.permissions)

  return useMutation({
    mutationKey: DEVICE_WRITE_MUTATION_KEY,
    mutationFn: updateDevicePulses,
    onSuccess: (deviceData) => {
      setDevice(queryClient, mapDevice(deviceData, permissions))
      notifySuccess('Device pulse updated successfully')
    },
    onError: showDeviceError,
  })
}

export const useDeviceValvesUpdate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: DEVICE_WRITE_MUTATION_KEY,
    mutationFn: updateDeviceValves,
    onSuccess: async (valveStates, { deviceId, idType }) => {
      const dUuid =
        idType === DeviceIDTypes.dUuid
          ? deviceId
          : queryClient
              .getQueryData<Device[]>(DEVICES_QUERY_KEY)
              ?.find((device) =>
                idType === DeviceIDTypes.deviceId
                  ? device.deviceId === deviceId
                  : String(device.dlId) === deviceId,
              )?.dUuid

      if (dUuid) {
        patchDevice(queryClient, dUuid, (device) => ({
          ...device,
          valves: Object.fromEntries(
            Object.values(device.valves).map((valve) => [
              valve.key,
              {
                ...valve,
                state: valveStates[valve.key] ?? valve.state,
              },
            ]),
          ) as Device['valves'],
        }))
      } else {
        await queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY, exact: true })
      }

      notifySuccess('Valves state updated successfully!')
    },
    onError: showDeviceError,
  })
}

export const useDeviceRefresh = () => {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((state) => state.permissions)

  return useMutation({
    mutationKey: DEVICE_WRITE_MUTATION_KEY,
    mutationFn: refreshDevice,
    onSuccess: (deviceData) => {
      setDevice(queryClient, mapDevice(deviceData, permissions))
      notifySuccess('Device data refreshed successfully')
    },
    onError: showDeviceError,
  })
}

const getByDeviceId = async (deviceId: string, permissions: TokenPermission[], signal: AbortSignal): Promise<Device> => {
  const response = await backend.get(`/devices/${deviceId}`, {
    signal,
    params: { idType: DeviceIDTypes.dUuid }
  })
  return mapDevice(response.data, permissions)
  // return camelcaseKeys(response.data, {deep: true})
}

export const useGetValveSchedule = (dUuid: string) => {
  return useQuery<ValveSchedule[]>({
    queryKey: ['valveSchedule', dUuid],
    queryFn: ({ signal }) => getValveSchedule(dUuid, signal),
    enabled: !!dUuid,
    staleTime: 1000 * 60,
  })
}

const getValveSchedule = async (dUuid: string, signal: AbortSignal): Promise<ValveSchedule[]> => {
  const response = await backend.get(
    `/devices/${dUuid}/valve_schedule`, {
    signal,
    params: { idType: DeviceIDTypes.dUuid }
  })
  return response.data
}

// Which endpoint family a valve action goes to. Both families take the same bodies and return
// the same shapes: provisioned devices are addressed by d_uuid through /devices with an idType,
// test devices by device ID through /test_devices with none.
export type ValveTarget =
  | { kind: 'production', dUuid: string }
  | { kind: 'test', deviceId: string }

// Stable for the same device across renders, unlike a target object built inline — use it
// wherever a target would otherwise be a hook dependency or part of a query key.
export const valveTargetKey = (target: ValveTarget) =>
  target.kind === 'production' ? `production:${target.dUuid}` : `test:${target.deviceId}`

const valveEndpoint = (target: ValveTarget, path: string) =>
  target.kind === 'production'
    ? {
      url: `/devices/${target.dUuid}/${path}`,
      params: { idType: DeviceIDTypes.dUuid },
    }
    : {
      url: `/test_devices/${target.deviceId}/${path}`,
      params: undefined,
    }

// Writes valve state into whichever cache holds the target's device: the provisioned device
// caches, or the test device list the Test Bench modal derives its device from.
const patchValves = (
  queryClient: QueryClient,
  target: ValveTarget,
  patch: (device: DeviceBase) => Partial<Pick<DeviceBase, 'valves' | 'valveRecovery'>>,
) => {
  if (target.kind === 'production') {
    patchDevice(queryClient, target.dUuid, (device) => ({ ...device, ...patch(device) }))
    return
  }

  queryClient.setQueryData<TestDevice[]>(TEST_DEVICES_QUERY_KEY, (devices) =>
    devices?.map((device) =>
      device.deviceId === target.deviceId ? { ...device, ...patch(device) } : device,
    ),
  )
}

// Read-only diagnostic snapshot (commissioning, extended state, and last recovery sweep
// per valve). Gated on `enabled` so it's only fetched when the advanced-data view is
// actually opened, not on every device load.
export const useValveStatus = (target: ValveTarget, enabled: boolean) => {
  const hasDeviceId = target.kind === 'production' ? !!target.dUuid : !!target.deviceId

  return useQuery<ValveStatusAPI>({
    queryKey: ['valveStatus', valveTargetKey(target)],
    queryFn: ({ signal }) => getValveStatus(target, signal),
    enabled: enabled && hasDeviceId,
    staleTime: 1000 * 60,
  })
}

const getValveStatus = async (target: ValveTarget, signal: AbortSignal): Promise<ValveStatusAPI> => {
  const {
    url,
    params,
  } = valveEndpoint(target, 'valve_status')
  const response = await backend.get(url, {
    signal,
    params,
  })
  return response.data
}

export const useValveScheduleUpdate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateValveSchedule,
    onSuccess: (data, { dUuid }) => {
      queryClient.setQueryData(['valveSchedule', dUuid], data)
    }
  })
}

const updateValveSchedule = async (
  data: ValveScheduleCreate & Pick<ValveSchedule, 'id'>,
): Promise<ValveSchedule[]> => {
  const {dUuid, id, ...update} = data
  const response = await backend.patch(
    `/devices/${dUuid}/valve_schedule/${id}`,
    update,
    { params: { idType: DeviceIDTypes.dUuid } },
  )
  return response.data
}

export const useValveScheduleCreate = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createValveSchedule,
    // The API returns the device's full schedule list, so the response replaces the cache.
    // Parallel creates can resolve out of order and leave an older snapshot here — callers
    // creating a batch invalidate ['valveSchedule', dUuid] once it settles.
    onSuccess: (data, { dUuid }) => {
      queryClient.setQueryData(['valveSchedule', dUuid], data)
    },
  })
}

export const useValveSettingsUpdate = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: DEVICE_WRITE_MUTATION_KEY,
    mutationFn: updateValveSettings,
    onSuccess: (valveSettings, { dUuid }) => {
      queryClient.setQueryData(valveSettingsQueryKey(dUuid), valveSettings)
    }
  })
}

export const useValveSettings = (dUuid: string, enabled: boolean) => {
  return useQuery<ValveSettingsAPI[]>({
    queryKey: valveSettingsQueryKey(dUuid),
    queryFn: ({ signal }) => getValveSettings(dUuid, signal),
    enabled: enabled && !!dUuid,
    staleTime: 1000 * 60,
  })
}

export const hasValveSettingsCache = (queryClient: QueryClient, dUuid: string) =>
  queryClient.getQueryState<ValveSettingsAPI[]>(valveSettingsQueryKey(dUuid))?.status === 'success'

export const refreshValveSettings = (queryClient: QueryClient, dUuid: string) => {
  return queryClient.fetchQuery({
    queryKey: valveSettingsQueryKey(dUuid),
    queryFn: ({ signal }) => getValveSettings(dUuid, signal),
    staleTime: 0,
  })
}

const getValveSettings = async (
  dUuid: string,
  signal: AbortSignal,
): Promise<ValveSettingsAPI[]> => {
  const response = await backend.get(
    `/devices/${dUuid}/valve_settings`,
    {
      signal,
      params: { idType: DeviceIDTypes.dUuid },
    },
  )
  return response.data
}

const createValveSchedule = async (data: ValveScheduleCreate): Promise<ValveSchedule[]> => {
  const { dUuid, ...payload } = data
  const response = await backend.put(
    `/devices/${dUuid}/valve_schedule`,
    payload,
    { params: { idType: DeviceIDTypes.dUuid } },
  )
  return response.data
}

export const useValveScheduleDelete = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteValveSchedule,
    onSuccess: (_, { dUuid, id }) => {
      queryClient.setQueryData(['valveSchedule', dUuid], (prev: ValveSchedule[] = []) => {
        return prev.filter(schedule => schedule.id !== id)
      })
    },
  })
}

const deleteValveSchedule = async ({ dUuid, id }: Pick<ValveSchedule, 'dUuid' | 'id'>): Promise<void> => {
  await backend.delete(
    `/devices/${dUuid}/valve_schedule/${id}`,
    { params: { idType: DeviceIDTypes.dUuid } },
  )
}

export type ValveProbeResult = ValveStateExtendedSlots & {
  valveRecovery: ValveRecoveryPolicy
}

export const useValveProbe = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: DEVICE_WRITE_MUTATION_KEY,
    mutationFn: probeValves,
    onSuccess: ({ valveRecovery, ...extended }, target) => {
      patchValves(queryClient, target, (device) => ({
        valves: applyValveStateExtended(device.valves, extended),
        valveRecovery,
      }))
    },
  })
}

// A recovery action, not a read — it blocks the device's main loop for up to ~9 s and
// energises drive lines, so it can physically move a valve. The backend refuses it while
// any valve is mid-travel or commissioning is active. Never poll this.
const probeValves = async (target: ValveTarget): Promise<ValveProbeResult> => {
  const {
    url,
    params,
  } = valveEndpoint(target, 'valve_probe')
  const response = await backend.get(url, {
    params,
    timeout: 30_000,
  })
  return response.data
}

export interface ValveRecoveryPolicyUpdate {
  target: ValveTarget
  policy: ValveRecoveryPolicy
}

// The PATCH response is narrower than the GET — policy and manual only, no type/band/travelMs.
export interface ValveRecoveryPolicySet {
  policy: ValveRecoveryPolicy
  manual: number
}

export const useValveRecoveryPolicyUpdate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: DEVICE_WRITE_MUTATION_KEY,
    mutationFn: updateValveRecoveryPolicy,
    onSuccess: ({ policy }, { target }) => {
      patchValves(queryClient, target, () => ({ valveRecovery: policy }))
    },
  })
}

// Talks to the device over Azure IoT rather than the database, so this is slower than a
// normal write and fails when the device is unreachable.
const updateValveRecoveryPolicy = async (
  { target, policy }: ValveRecoveryPolicyUpdate,
): Promise<ValveRecoveryPolicySet> => {
  const {
    url,
    params,
  } = valveEndpoint(target, 'valve_recovery_policy')
  const response = await backend.patch(url, { policy }, { params })
  return response.data
}

// Upsert per valveId — valves absent from the array and fields omitted from an entry are
// both left untouched server-side. Send null explicitly to clear a field. Returns the
// device's complete valve_settings list, not just the entries sent.
const updateValveSettings = async (data: DeviceValvesUpdate): Promise<ValveSettingsAPI[]> => {
  const { dUuid, valveSettings } = data
  const response = await backend.put(
    `/devices/${dUuid}/valve_settings`,
    valveSettings,
    { params: { idType: DeviceIDTypes.dUuid } },
  )
  return response.data
}

const resetDevice = async ({ deviceId, idType }: DeviceByIdRequest): Promise<void> => {
  await backend.patch(
    `/devices/${deviceId}/reset`,
    { key: 'Reset', value: 1 },
    { params: { idType } },
  )
}

const updateFirmware = async ({
  deviceId,
  idType,
  firmwareVersion,
}: FirmwareUpdate): Promise<void> => {
  await backend.patch(
    `/devices/${deviceId}/firmware_update`,
    { bin_file_name: firmwareVersion },
    { params: { idType } },
  )
}

const commissionValves = async (target: ValveTarget): Promise<void> => {
  const {
    url,
    params,
  } = valveEndpoint(target, 'commission_valves')
  await backend.put(url, null, { params })
}

const promoteToStaged = async (deviceId: string): Promise<void> => {
  await backend.put(`/device_swap/test_device_to_staged_devices/${deviceId}`)
}

// Test devices have their own command endpoints. They take the device ID only — a test device
// has no d_uuid, so there is no idType — and the reset takes no body, unlike /devices reset.
const resetTestDevice = async (deviceId: string): Promise<void> => {
  await backend.patch(`/test_devices/${deviceId}/reset`)
}

const updateTestDeviceFirmware = async ({
  deviceId,
  binFileName,
}: TestDeviceFirmwareUpdate): Promise<void> => {
  await backend.patch(
    `/test_devices/${deviceId}/firmware_update`,
    { bin_file_name: binFileName },
  )
}

const getTestDevice = async (deviceId: string): Promise<TestDeviceAPI> => {
  const response = await backend.get<TestDeviceAPI>(`/test_devices/${deviceId}`)
  return response.data
}

export const useDeviceReset = () => {
  return useMutation({
    mutationKey: DEVICE_MESSAGE_MUTATION_KEY,
    mutationFn: resetDevice,
    onSuccess: () => {
      notifySuccess('Device reset sent successfully')
    },
    onError: showDeviceError,
  })
}

export const useDeviceFirmwareUpdate = () => {
  return useMutation({
    mutationKey: DEVICE_MESSAGE_MUTATION_KEY,
    mutationFn: updateFirmware,
    onSuccess: () => {
      notifySuccess('Device Firmware update sent successfully')
    },
    onError: showDeviceError,
  })
}

export const useValveCommission = () => {
  return useMutation({
    mutationKey: DEVICE_MESSAGE_MUTATION_KEY,
    mutationFn: commissionValves,
  })
}

export const useTestDeviceReset = () => {
  return useMutation({
    mutationKey: DEVICE_MESSAGE_MUTATION_KEY,
    mutationFn: resetTestDevice,
    onSuccess: () => {
      notifySuccess('Device reset sent successfully')
    },
    onError: showDeviceError,
  })
}

export const useTestDeviceFirmwareUpdate = () => {
  return useMutation({
    mutationKey: DEVICE_MESSAGE_MUTATION_KEY,
    mutationFn: updateTestDeviceFirmware,
    onSuccess: () => {
      notifySuccess('Device Firmware update sent successfully')
    },
    onError: showDeviceError,
  })
}

// Writes the refreshed device into the test device list rather than a per-device query: the
// Test Bench modal derives its device from that list, so the table row and the open modal
// update together.
export const useTestDeviceRefresh = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: DEVICE_WRITE_MUTATION_KEY,
    mutationFn: getTestDevice,
    onSuccess: (deviceData) => {
      const refreshed: TestDevice = mapUnprovisionedDevice(deviceData)
      queryClient.setQueryData<TestDevice[]>(TEST_DEVICES_QUERY_KEY, (devices) =>
        devices?.map((device) => (device.deviceId === refreshed.deviceId ? refreshed : device)),
      )
      notifySuccess('Device data refreshed successfully')
    },
    onError: showDeviceError,
  })
}

export const usePromoteToStaged = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: DEVICE_WRITE_MUTATION_KEY,
    mutationFn: promoteToStaged,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['devices', 'test'] }),
        queryClient.invalidateQueries({ queryKey: ['devices', 'staged'] }),
      ])
      notifySuccess('Device staged successfully')
    },
    onError: showDeviceError,
  })
}

const buildDeviceAddRequest = (device: NewDevice): DeviceAdd => ({
  deviceName: device.deviceName,
  deviceId: device.deviceId,
  currency: device.currency,
  installDate: device.installDate,
  cost: device.cost,
  dataAcqMode: device.dataAcqMode,
  serverPulseControl: device.serverPulseControl,
  pulsesPerLiter: device.pulsesPerLiter,
  location: device.location,
  timezone: device.timezone,
  sectorType: device.sectorType,
  deviceState: device.deviceState,
  hot: device.hot,
  uploadFreqMins: device.uploadFreqMins,
  uom: device.uom,
})

export const useAddDeviceToLocationMutation = () => {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((state) => state.permissions)

  return useMutation({
    mutationKey: DEVICE_WRITE_MUTATION_KEY,
    mutationFn: async ({ device, locationId }: { device: NewDevice; locationId: string }) => {
      const response = await backend.patch<DeviceAPI>(
        `/customer_locations/${locationId}/devices`,
        buildDeviceAddRequest(device),
      )
      return response.data
    },
    onSuccess: async (deviceResponse) => {
      setDevice(queryClient, mapDevice(deviceResponse, permissions))
      await queryClient.invalidateQueries({ queryKey: STAGED_DEVICES_QUERY_KEY })
      notifySuccess('Device created successfully')
    },
    onError: showDeviceError,
  })
}

export const useSwapDeviceMutation = () => {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((state) => state.permissions)

  return useMutation({
    mutationKey: DEVICE_WRITE_MUTATION_KEY,
    mutationFn: async ({ origin, destination }: { origin: Device; destination: StagedDevice }) => {
      const response = await backend.put<DeviceAPI>(
        `/device_swap/live_device_swap_out/${origin.deviceId}?new_device=${destination.deviceId}`,
      )
      return {
        origin,
        device: mapDevice(response.data, permissions),
      }
    },
    onSuccess: async ({ origin, device }) => {
      setDevice(queryClient, device, origin.dUuid)

      if (origin.dUuid !== device.dUuid) {
        queryClient.removeQueries({
          queryKey: ['devices', 'DeviceId', origin.dUuid],
          exact: true,
        })
      }

      await queryClient.invalidateQueries({ queryKey: STAGED_DEVICES_QUERY_KEY })
      notifySuccess('Device swapped successfully')
    },
    onError: showDeviceError,
  })
}

export const useRemoveDeviceFromLocationMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: DEVICE_WRITE_MUTATION_KEY,
    mutationFn: async ({ device, locationId }: { device: Device; locationId: string }) => {
      await backend.delete(`/customer_locations/${locationId}/devices`, {
        data: { id: device.deviceId },
      })
      return device
    },
    onSuccess: async (device) => {
      queryClient.removeQueries({
        queryKey: ['devices', 'DeviceId', device.dUuid],
        exact: true,
      })
      await queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY, exact: true })
    },
    onError: showDeviceError,
  })
}


// Shared rather than observer-local, and deliberately so: every consumer asks "did the last
// device write or message fail?", not "did mine fail?". See device-zustand-migration-plan.md:402.
export const useDeviceQueryMutationState = () => {
  const deviceUpdating = useIsMutating({ mutationKey: DEVICE_WRITE_MUTATION_KEY }) > 0
  const deviceSendingMessage = useIsMutating({ mutationKey: DEVICE_MESSAGE_MUTATION_KEY }) > 0
  const writeMutationStates = useMutationState({
    filters: { mutationKey: DEVICE_WRITE_MUTATION_KEY },
    select: (mutation) => mutation.state,
  })
  const messageMutationStates = useMutationState({
    filters: { mutationKey: DEVICE_MESSAGE_MUTATION_KEY },
    select: (mutation) => mutation.state,
  })
  const latestMutationState = [...writeMutationStates, ...messageMutationStates].reduce(
    (latest, mutationState) =>
      mutationState.submittedAt > (latest?.submittedAt ?? 0)
        ? mutationState
        : latest,
    undefined as typeof writeMutationStates[number] | undefined,
  )

  return {
    deviceUpdating,
    deviceSendingMessage,
    error: latestMutationState?.status === 'error',
  }
}

// The devices visible under the current selection: account membership intersected with
// selected-location membership.
//
// Membership sets, not scans: this predicate runs once per device, and a linear lookup
// through every selected location on each pass is O(devices × locations) on large tenants.
//
// Note what this does *not* do. The device list depends on the account and location
// selections for its *data*, but device loading, error and access-denied remain device
// concerns — the facade never folded parent status into them and neither does this.
export const useVisibleDevices = () => {
  const { data } = useDevicesQuery()
  const allDevices = data ?? EMPTY_DEVICES
  const selectedAccountIds = useSelectedAccountIds()
  const selectedLocations = useSelectedLocations()

  const selectedAccountIdSet = useMemo(
    () => new Set(selectedAccountIds),
    [selectedAccountIds],
  )
  const selectedLocationIdSet = useMemo(
    () => new Set(selectedLocations.map((location) => location.id)),
    [selectedLocations],
  )

  return useMemo(
    () =>
      allDevices.filter(
        (device) =>
          selectedAccountIdSet.has(device.accountId) &&
          selectedLocationIdSet.has(device.deviceLocationId),
      ),
    [allDevices, selectedAccountIdSet, selectedLocationIdSet],
  )
}

// `selectedDeviceIds === null` means "all devices".
//
// Known inconsistency, reproduced as-is: the store holds `device.deviceId`, which is
// mutable — `dUuid` is the stable identifier and every alert-side visibility filter already
// moved to it. Changing the store's identifier is a selection-semantics change and needs its
// own item; doing it inside this relocation would make a reselection bug untraceable.
export const useSelectedDevices = () => {
  const { data } = useDevicesQuery()
  const allDevices = data ?? EMPTY_DEVICES
  const selectedDeviceIds = useDeviceStore((state) => state.selectedDeviceIds)

  return useMemo(
    () =>
      selectedDeviceIds === null
        ? allDevices
        : allDevices.filter((device) => selectedDeviceIds.includes(device.deviceId)),
    [allDevices, selectedDeviceIds],
  )
}

// "Is the device list moving?" — the facade's `loadingDevices`, which is `isLoading` OR
// `isFetching` and so stays true through a refetch, not just the first load. Distinct from
// the query's own `isLoading`, which the facade exposed separately as `loading`; consumers
// that want first-load-only should read that from useDevicesQuery directly.
//
// A hook rather than the expression repeated at fourteen call sites, for the same reason
// useLocationsLoading is one: a derived boolean is a concept, and spelling it out per call
// site invites one of them to drift.
export const useDevicesLoading = () => {
  const {
    isFetching,
    isLoading,
  } = useDevicesQuery()

  return isLoading || isFetching
}

// An empty device list has three causes that are indistinguishable by length alone: the
// scope gate held the request back, the server refused it, or the account genuinely has no
// devices. The first two are the same thing to a user — "you cannot see this" — and are the
// only ones worth telling them about.
export const useDeviceAccessDenied = () => {
  const { error } = useDevicesQuery()
  const canReadProdDevices = useCanReadProdDevices()

  return !canReadProdDevices || isForbiddenError(error)
}

// The facade's `loadDevices`, which had no mutation-hook equivalent because it is not a
// mutation. Takes no arguments and returns a promise, matching the old call signature, so
// the table's refresh control keeps working unchanged. Exact-keyed: the device list only,
// not the timezone/test/staged queries that share the `devices` prefix.
export const useDevicesRefresh = () => {
  const queryClient = useQueryClient()

  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY, exact: true }),
    [queryClient],
  )
}
